import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';

/* Two ways in for a buyer who is not ready to talk to sales yet: read the
 rollout playbook, or watch the tour. Deliberately only two, so the choice
 stays a choice. */

const TOUR_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/cf56c2dd-56b8-4eeb-b4d6-b164d4ddd9de.jpg';
const CALENDLY = 'https://calendly.com/tonyennis/ai-adoption-for-your-business-clone';
function CtaRow({
  label,
  tone
}: {
  label: string;
  tone: 'light' | 'dark';
}) {
  return <span className="mt-6 inline-flex items-center gap-3 font-semibold" data-id="element-2489" data-mp="2re2w91pb5ayz">
      <span className={`flex h-9 w-9 items-center justify-center rounded-full transition-transform group-hover:translate-x-0.5 ${tone === 'dark' ? 'bg-white/20 text-white' : 'bg-black/10 text-[#2B211A]'}`} data-id="element-2490" data-mp="2mf9id863zhya">
        <ArrowRightIcon size={16} strokeWidth={2.5} data-id="element-2491" data-mp="1b2yyxsd98y6i" />
      </span>
      {label}
    </span>;
}

/* A stylised spread of the handbook — two pages, one cover and one inner
 page — cropped by the card so it reads as an object sitting in the tile. */
function HandbookVisual() {
  return <div className="mt-auto flex justify-center gap-3 pt-10" data-id="element-2492" data-mp="mdpawmyb1skh">
      <div className="w-[46%] rounded-t-sm bg-[#2B211A] p-4 shadow-[0_-8px_30px_-10px_rgba(0,0,0,0.3)]" data-id="element-2493" data-mp="7hzt7105k1lk">
        <p className="font-title text-lg font-medium leading-tight text-white" data-id="element-2494" data-mp="cgb47f26oi5d">
          Transformation
        </p>
        <p className="mt-16 font-title text-base font-medium leading-tight text-white" data-id="element-2495" data-mp="3utshepdolb78">
          20% technology
          <br data-id="element-2496" data-mp="3gxtyk68jpjl" />
          and 80% people
        </p>
      </div>
      <div className="w-[46%] rounded-t-sm bg-white p-4 shadow-[0_-8px_30px_-10px_rgba(0,0,0,0.2)]" data-id="element-2497" data-mp="3kx5yw78tuw8d">
        <p className="text-[13px] font-bold text-[#2B211A]" data-id="element-2498" data-mp="3pv7icta60xkd">
          Manage change actively
        </p>
        <div className="mt-3 space-y-1.5" aria-hidden="true" data-id="element-2499" data-mp="3bvpmjtygck1i">
          {[100, 92, 96, 78, 100, 88, 94, 70, 98, 84].map((w, i) => <span key={i} className="block h-[3px] rounded-full bg-neutral-200" style={{
          width: `${w}%`
        }} data-id="element-2500" data-mp="3fw9kgzqunrop" data-mpi={i} />)}
        </div>
      </div>
    </div>;
}
export function GuidedStart() {
  return <section className="px-6 py-20 md:px-10 md:py-28" data-id="element-2501" data-mp="1hnc2zg3iadrg">
      <div className="mx-auto max-w-[1100px]" data-id="element-2502" data-mp="1corc47a596fo">
        <h2 className="font-title text-3xl font-medium leading-tight tracking-tight md:text-5xl" data-id="element-2503" data-mp="1cpkaokyy8pmq">
          Guiding you step by step.
          <span className="block text-neutral-400" data-id="element-2504" data-mp="17o3bqv00zd8s">
            Learn how Giga can work for your organisation.
          </span>
        </h2>

        <div className="mt-12 grid gap-5 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]" data-id="element-2505" data-mp="20ebtilqnfvmu">
          {/* Handbook */}
          <a href={CALENDLY} target="_blank" rel="noopener noreferrer" className="group flex flex-col overflow-hidden rounded-2xl bg-[#D5DCDA] p-7 pb-0 text-[#2B211A] md:p-8 md:pb-0" data-id="element-2506" data-mp="1ve7hp90orp2y">
            <h3 className="font-title text-xl font-medium tracking-tight md:text-2xl" data-id="element-2507" data-mp="k1enzsoc4qk9">
              AI Adoption Handbook
            </h3>
            <p className="mt-2 max-w-[34ch] leading-relaxed text-[#2B211A]/60" data-id="element-2508" data-mp="g0uur06vu73e">
              Our best practices from supporting hundreds of AI rollouts.
            </p>
            <CtaRow label="Get the handbook" tone="light" data-id="element-2509" data-mp="2voia5eg0a3gt" />
            <HandbookVisual data-id="element-2510" data-mp="30n1uxbiv597l" />
          </a>

          {/* Product tour */}
          <Link to="/demo" className="group relative flex min-h-[420px] flex-col justify-end overflow-hidden rounded-2xl p-7 text-white md:p-8" data-id="element-2511" data-mp="30kywc33na7r9">
            <img src={TOUR_IMAGE} alt="A monitor on a desk in a warm, sunlit office" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" data-id="element-2512" data-mp="3adeg95yi56es" />
            {/* Scrim keeps the copy legible against the photo. */}
            <div className="absolute inset-0 bg-black/45" aria-hidden="true" data-id="element-2513" data-mp="3oc4amdtxsrzg" />
            <div className="relative" data-id="element-2514" data-mp="3oa0922jig52c">
              <h3 className="font-title text-xl font-medium tracking-tight md:text-2xl" data-id="element-2515" data-mp="1p01u9cgm0dq0">
                Watch product tour
              </h3>
              <p className="mt-2 max-w-[38ch] leading-relaxed text-white/70" data-id="element-2516" data-mp="1tylgzf311tjf">
                See a 4-minute overview of Giga's core products.
              </p>
              <CtaRow label="See Giga in action" tone="dark" data-id="element-2517" data-mp="tpv8mxfolziu" />
            </div>
          </Link>
        </div>
      </div>
    </section>;
}